/**
 * handlers.appointments.ts — offline replay for TERMINE (appointments).
 *
 * Charter: ONE bounded context — the rep's calendar appointments. Owns the queue
 * payload shapes and the replay handlers for create, edit and cancel, and nothing
 * else. Registered by the `./handlers` facade.
 */

import { registerQueueHandler } from './offlineQueue'
import { createAppointment, updateAppointment, cancelAppointment } from '../api/appointments'
import { QUEUE_TYPES } from './queueTypes'

export interface AppointmentCreatePayload {
  owner_email: string
  title: string
  start_time: string
  end_time: string
  location: string | null
}

export interface AppointmentCancelPayload {
  id: string
}

/**
 * Appointment UPDATE (edit of an already-synced Termin) replay. The synced-edit
 * branch used to `.catch(toast)` with NO durable path, so a transient PATCH
 * failure LOST the edit outright (localStorage held the new values; SL kept the
 * old ones forever). The producer now travels the `durableSubmit` single-submit
 * chokepoint, which persists this payload BEFORE the network — so the fields are
 * stored FLAT (not nested under a `patch` key) exactly as the chokepoint's
 * `attempt` receives them, and a replay re-PATCHes `/v1/appointments/{id}`
 * verbatim.
 */
export interface AppointmentUpdatePayload {
  id: string
  title: string
  start_time: string
  end_time: string
  location: string | null
}

export function registerAppointmentHandlers(): void {
  registerQueueHandler(QUEUE_TYPES.APPOINTMENT_CREATE, async (payload) => {
    const p = payload as AppointmentCreatePayload
    await createAppointment(p)
  })

  // Termin edit (already-synced appointment) — replay the PATCH.
  registerQueueHandler(QUEUE_TYPES.APPOINTMENT_UPDATE, async (payload) => {
    const p = payload as AppointmentUpdatePayload
    await updateAppointment(p.id, {
      title: p.title,
      start_time: p.start_time,
      end_time: p.end_time,
      location: p.location,
    })
  })

  registerQueueHandler(QUEUE_TYPES.APPOINTMENT_CANCEL, async (payload) => {
    const p = payload as AppointmentCancelPayload
    await cancelAppointment(p.id)
  })
}
